/**
 * kc-image-revival.js
 *
 * Ensures images load progressively as the visitor scrolls, and recovers
 * from failed/stuck loads automatically. Addresses three pain points:
 *
 *   1) PROGRESSIVE LOADING — Chrome's native `loading="lazy"` threshold
 *      is roughly the viewport height, which can feel "popping in" on
 *      slow connections, and sometimes never triggers if the image is
 *      inside a parent that becomes visible via JS (e.g. carousel tab
 *      hydration) without a scroll event near it.
 *      We observe every lazy image with a generous rootMargin so the
 *      browser starts fetching ~1500px before the image enters the
 *      viewport.
 *
 *   2) ERROR RETRY — when an image fires `error` (network blip, 5xx
 *      from the CDN, transient timeout), the browser leaves it in a
 *      broken state forever. We retry up to 3 times with backoff,
 *      adding a cache-busting query param to bypass any failed-response
 *      cache.
 *
 *   3) DYNAMIC INSERTIONS — MutationObserver picks up images added
 *      after initial parse (carousel tab hydration, AJAX swaps, Shopify
 *      editor re-renders) and applies the same protections to them.
 *
 *   4) HOVER VIDEOS — product-card hover videos (video[data-hover-video])
 *      play on mouseenter / pause on mouseleave so they download zero bytes
 *      until an actual hover (and never on touch / mobile).
 *
 * No external dependencies. Runs as a deferred ES module so it doesn't
 * block first paint.
 */

(function () {
  const LAZY_PRELOAD_MARGIN = '1500px';
  const RETRY_ATTEMPTS = 3;
  const RETRY_DELAY = 1500; // base ms, multiplied by attempt number

  // UGC video section on the homepage. Scoped by id substring because sections
  // defined in a JSON template render as shopify-section-template--<n>__<key>.
  const UGC_SECTION_SELECTOR = '[id*="kc_custom_section_L3My48"]';
  const UGC_CLIP_SECONDS = 4;

  /**
   * Promotes a lazy image to eager so the browser starts fetching it.
   * No-op if the image is already eager or already loaded.
   */
  function promoteToEager(img) {
    if (img.loading === 'eager' || img.complete) return;
    img.loading = 'eager';
  }

  /**
   * Retry handler — re-assign the src with a cache-buster after a delay.
   * Subsequent attempts use exponential backoff (1.5s, 3s, 4.5s).
   */
  function handleImageError(event) {
    const img = event.target;
    if (!img || img.tagName !== 'IMG' || !img.src) return;

    // Ignore images that don't have a real src (placeholders, data URIs, etc.)
    if (img.src.startsWith('data:')) return;

    const attempts = parseInt(img.dataset.kcRetryAttempts || '0', 10);
    if (attempts >= RETRY_ATTEMPTS) return;

    img.dataset.kcRetryAttempts = String(attempts + 1);
    const delay = RETRY_DELAY * (attempts + 1);

    setTimeout(function () {
      // Re-check: maybe a different code path already loaded it
      if (img.complete && img.naturalWidth > 0) return;
      try {
        const url = new URL(img.src, location.href);
        url.searchParams.set('_r', String(Date.now()));
        img.src = url.toString();
      } catch (_) {
        // src wasn't a valid URL — bail silently
      }
    }, delay);
  }

  let progressiveObserver = null;

  function getProgressiveObserver() {
    if (progressiveObserver) return progressiveObserver;
    if (!('IntersectionObserver' in window)) return null;

    progressiveObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          promoteToEager(entry.target);
          progressiveObserver.unobserve(entry.target);
        });
      },
      { rootMargin: LAZY_PRELOAD_MARGIN }
    );
    return progressiveObserver;
  }

  /**
   * Observe a single image for progressive loading.
   * Idempotent — calling twice on the same image is harmless.
   */
  function observeImage(img) {
    if (!img || img.tagName !== 'IMG') return;
    if (img.dataset.kcRevivalObserved) return;
    img.dataset.kcRevivalObserved = '1';

    if (img.loading === 'lazy' && !img.complete) {
      const io = getProgressiveObserver();
      if (io) io.observe(img);
    }
  }

  /**
   * Product-card hover videos (video[data-hover-video]) are rendered WITHOUT
   * autoplay and with preload="none", so they cost zero bytes until the
   * visitor actually hovers the card. Start playback on mouseenter, pause on
   * mouseleave. Using mouseenter/leave (not pointer events) keeps this to real
   * mouse hovers — touch devices never fetch the video. Idempotent.
   */
  function bindHoverVideo(video) {
    if (!video || video.dataset.kcHoverBound) return;
    video.dataset.kcHoverBound = '1';

    const card = video.closest('.kc-product-card') || video.parentElement;
    if (!card) return;

    card.addEventListener('mouseenter', function () {
      if (video.paused) video.play().catch(function () {});
    });
    card.addEventListener('mouseleave', function () {
      if (!video.paused) video.pause();
    });
  }

  /**
   * UGC video previews — homepage "UGC videos" section ONLY.
   * These are autoplay, muted, looping social clips. Left untouched, each one
   * downloads in full once scrolled into view (8 clips ≈ tens of MB).
   *
   * PREVIEW (default): capped to the first 4 seconds —
   *   - a `#t=0,4` media fragment makes the browser range-fetch only that slice
   *   - a `timeupdate` guard loops within the first 4s.
   * FULL (on click): a centered play button expands the clip to the full video
   *   with sound + native controls (the fragment/clamp are removed). The full
   *   bytes are only ever fetched if the visitor actually clicks.
   *
   * Scoped strictly to the UGC section via closest(), so no other video on the
   * site is affected. Idempotent.
   */
  function bindUgcPreview(video) {
    if (!video || video.dataset.kcUgcBound) return;
    if (!video.closest(UGC_SECTION_SELECTOR)) return; // UGC section only
    video.dataset.kcUgcBound = '1';

    const addFragment = function (url) {
      return !url || url.indexOf('#t=') !== -1 ? url : url + '#t=0,' + UGC_CLIP_SECONDS;
    };
    const stripFragment = function (url) {
      return url ? url.replace(/#t=[^#]*$/, '') : url;
    };
    const eachSrc = function (fn) {
      const sources = video.querySelectorAll('source');
      if (sources.length) sources.forEach(function (s) { if (s.getAttribute('src')) s.src = fn(s.src); });
      else if (video.getAttribute('src')) video.src = fn(video.src);
    };

    // --- PREVIEW: limit fetched bytes to the first few seconds. ---
    eachSrc(addFragment);
    video.setAttribute('preload', 'metadata');
    if (video.paused && video.currentTime === 0) { try { video.load(); } catch (e) {} }

    // Loop within the first 4s while in preview mode.
    video.addEventListener('timeupdate', function () {
      if (video.dataset.kcUgcFull === '1') return; // expanded → play full, no clamp
      if (video.currentTime >= UGC_CLIP_SECONDS) {
        video.currentTime = 0;
        if (video.paused) { const p = video.play(); if (p && p.catch) p.catch(function () {}); }
      }
    });

    // --- FULL: click to watch the whole clip with sound + controls. ---
    function expandToFull() {
      if (video.dataset.kcUgcFull === '1') return;
      video.dataset.kcUgcFull = '1';
      eachSrc(stripFragment); // restore full source range
      video.loop = false;
      video.controls = true;
      video.muted = false;
      video.setAttribute('preload', 'auto');
      try { video.load(); } catch (e) {}
      video.currentTime = 0;
      const p = video.play();
      if (p && p.catch) p.catch(function () {}); // if blocked, native controls let them play manually
    }

    // Centered play button = the click cue. The .video-block__inner wrapper is
    // already position:relative, so the button overlays the clip.
    const wrap = video.closest('.video-block__inner') || video.parentElement;
    if (wrap && !wrap.querySelector('.kc-ugc-play')) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kc-ugc-play';
      btn.setAttribute('aria-label', 'Bekijk de volledige video');
      btn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        expandToFull();
        btn.remove();
      });
      wrap.appendChild(btn);
    }
  }

  /**
   * Decorative autoplay videos — <video-media autoplay><video preload="none">,
   * emitted by snippets/kc-video.liquid (kc-video / kc-media blocks).
   *
   * theme.js starts them once, when they first enter the viewport, and never
   * stops them: scrolling down a page with several clips left every one of them
   * downloading and decoding at the same time (3–22 MB of video per visit in the
   * Oct 2026 audit). Without touching theme.js:
   *
   * A clip that scrolls out of view is paused, and resumed when it comes back.
   * Only clips WE paused are resumed, so the first play — and its autoplay-blocked
   * fallback — stays theme.js' job. A clip that is not muted is left alone: the
   * visitor turned its sound on, so it is theirs to stop.
   */
  const autoplayVideos = new WeakSet();
  const pausedOffscreen = new WeakSet();
  let autoplayObserver = null;

  function getAutoplayObserver() {
    if (autoplayObserver || !('IntersectionObserver' in window)) return autoplayObserver;
    autoplayObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (pausedOffscreen.has(video)) {
            pausedOffscreen.delete(video);
            if (video.paused) video.play().catch(function () {});
          }
        } else if (!video.paused && video.muted) {
          pausedOffscreen.add(video);
          video.pause();
        }
      });
    });
    return autoplayObserver;
  }

  function observeAutoplayVideo(video) {
    if (!video || video.tagName !== 'VIDEO' || autoplayVideos.has(video)) return;
    if (!video.closest('video-media[autoplay]')) return;
    autoplayVideos.add(video);

    const io = getAutoplayObserver();
    if (io) io.observe(video);
  }

  function init() {
    // 1) Observe all existing lazy images on the page.
    document.querySelectorAll('img[loading="lazy"]').forEach(observeImage);

    // 1b) Wire up hover-to-play for product-card hover videos.
    document.querySelectorAll('video[data-hover-video]').forEach(bindHoverVideo);

    // 1c) Decorative autoplay videos: pause while off screen.
    document.querySelectorAll('video-media[autoplay] video').forEach(observeAutoplayVideo);

    // 1d) Cap UGC-section videos to a 4s preview.
    document.querySelectorAll(UGC_SECTION_SELECTOR + ' video').forEach(bindUgcPreview);

    // 2) Watch for images added later (carousel tab hydration, AJAX,
    //    editor re-renders). MutationObserver fires for any DOM mutation
    //    under <body>; we filter to images.
    if ('MutationObserver' in window) {
      const mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return; // ELEMENT_NODE only
            if (node.tagName === 'IMG') {
              observeImage(node);
            } else if (node.tagName === 'VIDEO') {
              if (node.hasAttribute('data-hover-video')) bindHoverVideo(node);
              observeAutoplayVideo(node);
              bindUgcPreview(node);
            } else if (node.querySelectorAll) {
              node.querySelectorAll('img[loading="lazy"]').forEach(observeImage);
              node.querySelectorAll('video[data-hover-video]').forEach(bindHoverVideo);
              node.querySelectorAll('video').forEach(observeAutoplayVideo);
              node.querySelectorAll('video').forEach(bindUgcPreview);
            }
          });
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    // 3) Global error retry — capture phase so we catch errors from
    //    images at any depth, including those added dynamically.
    document.addEventListener('error', handleImageError, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
